<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

// A polished replacement for the native <select> used to wire a button to the
// screen it opens (or to "hand to agent" / "new screen").
const props = defineProps({
  modelValue: { type: [String, null], default: null }, // target screen id or null
  options: { type: Array, default: () => [] }, // [{ id, title }]
});
const emit = defineEmits(['update:modelValue', 'createNew']);
const { t } = useI18n();

const open = ref(false);
const root = ref(null);

const isLinked = computed(() => Boolean(props.modelValue));
const currentLabel = computed(() => {
  const found = props.options.find(o => o.id === props.modelValue);
  return found ? found.title : t('AUTO_REPLY_BOT_SETTINGS.LINK.TO_AGENT');
});

const toggle = () => {
  open.value = !open.value;
};
const pick = id => {
  emit('update:modelValue', id);
  open.value = false;
};
const pickAgent = () => {
  emit('update:modelValue', null);
  open.value = false;
};
const pickNew = () => {
  open.value = false;
  emit('createNew');
};

const onDocClick = e => {
  if (root.value && !root.value.contains(e.target)) open.value = false;
};
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>

<template>
  <div ref="root" class="relative min-w-[165px] flex-1">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-2 rounded-[10px] border px-3 py-2 text-[12.5px] font-bold outline-none transition-all"
      :class="
        isLinked
          ? 'border-transparent bg-n-brand/10 text-n-brand'
          : 'border-n-weak bg-n-alpha-black1 text-n-slate-12 hover:border-n-strong'
      "
      @click="toggle"
    >
      <span class="truncate">{{ currentLabel }}</span>
      <span
        class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-10 transition-transform"
        :class="open ? 'rotate-180' : ''"
      />
    </button>

    <div
      v-if="open"
      class="absolute inset-x-0 top-[calc(100%+7px)] z-40 max-h-[280px] overflow-y-auto rounded-[14px] border border-n-weak bg-n-solid-1 p-1.5 shadow-xl"
    >
      <button
        type="button"
        class="flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2.5 text-right text-[13px] font-semibold transition-colors hover:bg-n-brand/10 hover:text-n-brand"
        :class="!isLinked ? 'bg-n-brand/10 text-n-brand' : 'text-n-slate-12'"
        @click="pickAgent"
      >
        <span class="i-lucide-corner-down-left size-4 shrink-0" />
        <span class="flex-1 text-right">{{
          t('AUTO_REPLY_BOT_SETTINGS.LINK.TO_AGENT_FULL')
        }}</span>
        <span v-if="!isLinked" class="i-lucide-check size-4 shrink-0" />
      </button>

      <button
        v-for="opt in options"
        :key="opt.id"
        type="button"
        class="flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2.5 text-right text-[13px] font-semibold transition-colors hover:bg-n-brand/10 hover:text-n-brand"
        :class="
          modelValue === opt.id
            ? 'bg-n-brand/10 text-n-brand'
            : 'text-n-slate-12'
        "
        @click="pick(opt.id)"
      >
        <span class="i-lucide-message-square size-4 shrink-0 text-n-slate-10" />
        <span class="flex-1 truncate text-right">{{ opt.title }}</span>
        <span
          v-if="modelValue === opt.id"
          class="i-lucide-check size-4 shrink-0"
        />
      </button>

      <button
        type="button"
        class="mt-1 flex w-full items-center gap-2.5 rounded-[10px] border-t border-n-weak px-2.5 pb-2.5 pt-3 text-right text-[13px] font-semibold text-n-slate-11 transition-colors hover:text-n-brand"
        @click="pickNew"
      >
        <span class="i-lucide-plus size-4 shrink-0 text-n-brand" />
        <span class="flex-1 text-right">{{
          t('AUTO_REPLY_BOT_SETTINGS.LINK.NEW_SCREEN')
        }}</span>
      </button>
    </div>
  </div>
</template>
